import { AutoComplete, Input, InputRef, Row } from 'antd';
import { SearchIcon } from 'assets/Icons/AppointmentIcons';
import IconClose from 'assets/Icons/closeModal';
import { AxiosResponse } from 'axios';
import Spin from 'components/Spin';
import Text from 'components/Text';
import { debounce } from 'lodash';
import { forwardRef, useCallback, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { getCustomersFilter } from 'services/shop/apis';
import shopSelectors from 'services/shop/selectors';
import { ICustomerItemData } from 'services/shop/types/customer';
import styled from 'styled-components';
import { formatPhone } from 'utils/formatPhone';
import ButtonAddCustomer, { useButtonAddCustomerRef } from 'widgets/Customer/AddCustomer';
import NotFoundCustomerBox from 'widgets/Customer/NotFoundCustomerBox';
export const unknownCustomerId = '_unknownCustomerId';
export const unknownCustomerItem: ICustomerItemData = {
  id: unknownCustomerId,
  email: '',
  imageAvatarUrl: '',
  name: 'Unknown Customer',
  phone: '0000000000',
  numberVisit: 0,
  point: 0,
};
export const maskPhone = (val: string) => {
  if (!val) return '';
  return '(***) ***-' + val.slice(val.length - 4, val.length);
};
type ICusOption = {
  value: string;
  name: string;
  keywords: string[];
  label: JSX.Element;
};
type CustomerSearchInputProps = {
  v2NewApt?: boolean;
  value?: ICustomerItemData | null;
  onChange?: (data: ICustomerItemData | null) => void;
};

type Ref = {
  open: () => void;
};
export const useCustomerSearchInputRef = () => useRef<Ref>(null);

const CustomerSearchInput = forwardRef<Ref, CustomerSearchInputProps>(({ onChange = () => undefined, value }, ref) => {
  const inputRef = useRef<InputRef>(null);
  const [open, setOpen] = useState(false);
  const customerStore = shopSelectors.data.customers();
  const [customersFilter, setCustomersFilter] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const addCustomerRef = useButtonAddCustomerRef();
  // Remembers the search we already auto-opened "add new" for, to avoid re-opening it.
  const autoOpenAttemptedRef = useRef<string>('');
  const initialOptions = useMemo(() => {
    const data: ICusOption[] = customerStore.map(o => {
      const point = +o?.point || 0;
      const phone = maskPhone(o?.phone || '');
      return ({
        value: o.id,
        name: o.name || '',
        keywords: [o.name?.toLocaleLowerCase(), phone],
        label: renderLabel(o.name || '', point > 1 ? `${point} points` : point > 0 ? `${point} point` : '', phone),
      });
    });
    return data;
  }, [customerStore]);

  const [searchText, setSearchText] = useState<string>('');

  const optionsFilter = useMemo(() => customersFilter.map(o => {
    const point = +o?.point || 0;
    const phone = maskPhone(o?.phone || '');
    return ({
      value: o.id,
      name: o.name,
      label: renderLabel(o.name, point > 1 ? `${point} points` : point > 0 ? `${point} point` : '', phone),
    });
  }), [customersFilter]);

  const options = useMemo(() => {
    if (!searchText) return initialOptions;
    return optionsFilter;
  }, [initialOptions, searchText, optionsFilter]);

  const handleSwitchCustomer = (data: string) => {
    inputRef.current?.blur();
    setSearchText('');
    if (data === unknownCustomerId) {
      onChange(unknownCustomerItem);
      return;
    }
    const result = (searchText ? customersFilter : customerStore)?.find((item) => item.id === data);
    if (!result) return;
    onChange(result);
  };

  const onOpenAddNew = ({ phone, name }: { phone?: string, name?: string }) => {
    addCustomerRef.current?.open({ phone: phone ? formatPhone(phone) : '', name });
    setSearchText('');
  };

  const getList = useCallback(debounce(async (search: string) => {
    try {
      const res: AxiosResponse<{ data: { content: ICustomerItemData[] } }> = await getCustomersFilter(search);
      if (res.data.data.content) {
        setCustomersFilter(res.data.data.content);
        // Auto-open "add new customer" when a full 10-digit phone yields no match.
        const onlyDigits = (search || '').replace(/\D/g, '');
        if (onlyDigits.length >= 10 && res.data.data.content.length === 0) {
          if (autoOpenAttemptedRef.current !== search) {
            autoOpenAttemptedRef.current = search;
            onOpenAddNew({ phone: search });
          }
        }
      }
    } catch (error) { }
    finally {
      setLoading(false);
    }
  }, 500), []);

  useImperativeHandle(ref, () => ({
    open: () => {
      setOpen(true);
      inputRef.current?.focus();
    },
  }));

  return (
    <Container wrap={false} align={'middle'} style={{ gap: '4px' }} className='CustomerSearchInput ticketNumberInput' >
      <WrapperInputSearch className="container-input">
        <AutoComplete
          style={{ width: '100%' }}
          options={options}
          dropdownRender={(menu) => (<>
            {searchText && loading ? <Row align={'middle'} justify={'center'}><Text py={1.5}><Spin /></Text></Row> : menu}
          </>)}
          notFoundContent={<NotFoundCustomerBox searchText={searchText} onOpenAddNew={onOpenAddNew} />}
          onSelect={handleSwitchCustomer}
          value={searchText}
          open={open}
          onDropdownVisibleChange={setOpen}
          maxLength={11}
        >
          <Input
            ref={inputRef}
            className='search-input'
            placeholder={!value ? 'Search by customer\'s name/phone' : (`${value?.name} - ${value?.id !== unknownCustomerId ? maskPhone(value?.phone || '') : formatPhone('00000000000')}`)}
            prefix={<SearchIcon fill='#232F3E' />}
            onChange={e => {
              const text = e.target.value;
              if (text) setLoading(true);
              setSearchText(text);
              getList(text);
            }}
          />
        </AutoComplete>
        {!!value && <ButtonClear onClick={(e: any) => [e.stopPropagation(), onChange(null)]}>
          <IconClose />
        </ButtonClear>}
      </WrapperInputSearch>
      <ButtonAddCustomer v2 ref={addCustomerRef} model={'quick-booking-v2'} onChooseCustomer={onChange} isAuto />
    </Container>
  );
});
CustomerSearchInput.displayName = 'CustomerSearchInput';
export default CustomerSearchInput;
const ButtonClear = styled.button`
  position: absolute;
  right: 16px;
  z-index: 99;
  width: 32px;
  height: 32px;
`;
const WrapperInputSearch = styled.div` &.container-input {
  flex: 1;
  display: flex;
  align-items: center;
  position: relative;
  .search-input.ant-input-affix-wrapper {
    height: 56px;
    padding: 16px;
    gap: 10px;
    border: 1px solid #ADADAD;
    border-radius: 4px;
    background: #FFF;
    .ant-input-prefix {
      margin-inline-end: 10px;
    }
    input {
      font-family: Poppins;
      font-size: 18px;
      font-style: normal;
      font-weight: 500;
      line-height: normal;
      color: #1D2129;
      align-self: center;
    }
  }
}
`;

const Container = styled(Row)`
  flex: 1;
  .container-input {
    display: flex;
    flex: 1;
    align-items: center;
    position: relative;
    input.ant-input::placeholder {
      color: #64748B;
    }
  }
`;

const renderLabel = (name: string, point: string, tel: string) => (
  <div
    style={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
    }}
  >
    <div
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        flexDirection: 'column',
      }}
    >
      <Text variant="CONTENT_2" color="text_3">
        {name}
      </Text>
      <Text variant="BODY_1" color="text_2">
        {point}
      </Text>
    </div>
    <div>
      <Text variant="BODY_1" color="text_3">
        {tel}
      </Text>
    </div>
  </div>
);
